import { ref, onMounted, onUnmounted, type Ref } from 'vue'

export const useHeaderScroll = (headerRef: Ref<HTMLElement | null>) => {
    const isHidden = ref(false)
    let lastScroll = 0
    let accumulated = 0
    let headerHeight = 0 // 缓存 header 高度，避免每次滚动都读取
    let ticking = false // RAF 节流标志

    const HIDE_THRESHOLD = 80 // 向下累计多少像素才隐藏
    const SHOW_THRESHOLD = 5  // 向上多小都可以显示

    // 更新缓存的 header 高度
    const updateHeaderHeight = () => {
        if (headerRef.value) {
            headerHeight = headerRef.value.offsetHeight
        }
    }

    const handleScrollCore = () => {
        ticking = false
        const current = window.scrollY
        // 初始状态下或者未获取到元素时不处理
        if (!headerRef.value) return

        const delta = current - lastScroll

        /* ⭐ 兜底规则 1：到顶部必须显示 */
        if (current <= 0) {
            isHidden.value = false
            accumulated = 0
            lastScroll = current
            return
        }

        /* ⭐ 兜底规则 2：内容还没盖住 header，不隐藏（使用缓存的高度） */
        if (current < headerHeight) {
            isHidden.value = false
            accumulated = 0
            lastScroll = current
            return
        }

        /* 向下滚动 */
        if (delta > 0) {
            accumulated += delta
            if (accumulated > HIDE_THRESHOLD) {
                isHidden.value = true
            }
        }

        /* 向上滚动 */
        if (delta < 0) {
            accumulated = 0
            if (Math.abs(delta) >= SHOW_THRESHOLD) {
                isHidden.value = false
            }
        }

        lastScroll = current
    }

    const handleScroll = () => {
        // 使用 RAF 节流，避免频繁触发布局计算
        if (!ticking) {
            requestAnimationFrame(handleScrollCore)
            ticking = true
        }
    }

    onMounted(() => {
        // 初始化 lastScroll 防止页面刷新在中间位置时 delta 计算过大
        lastScroll = window.scrollY
        // 初始化缓存 header 高度
        updateHeaderHeight()
        window.addEventListener('scroll', handleScroll, { passive: true })
        // 窗口大小变化时更新 header 高度
        window.addEventListener('resize', updateHeaderHeight, { passive: true })
    })

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll)
        window.removeEventListener('resize', updateHeaderHeight)
    })

    return {
        isHidden
    }
}
